/**
 * `<MemberFullMap/>` — the member's read-oriented full map (B5.2).
 *
 * Issue #761 (Phase 2). The WHOLE tree, exactly the same data the lead sees
 * (§A8 — shared sight of the plan is the point), but oriented for a member:
 * their "parts" (assigned nodes + the subtrees beneath them) are highlighted
 * in indigo and kept bright; everything else stays visible but de-emphasised
 * as read-only context. This is collaborative sight, never surveillance — no
 * per-person tally, just "here is where my work fits in the whole" (§A9).
 *
 * It reuses the #759 `TaskCanvas` (never forks it) with its `highlightIds`
 * prop, and deliberately omits the editing gestures (no drag-to-reparent, no
 * inline state swatches surfaced via focus) — a member acts on a node by
 * opening it (the drawer), where actions are gated by `canAuthor` (B5.3).
 *
 * Faithful to `B5-2-full-map-member.svg`: highlighted "you" branch, dimmed
 * siblings, and the verbatim footer caption "your branch is highlighted; rest
 * is read-only context".
 *
 * Editorial revamp (#1043, Wave 3, B5.2): theme-aware via `useMapTheme()`.
 * The CANVAS itself is already editorial (PR-A `TaskCanvas`/`TaskNodeCard` —
 * lit/dimmed treatment lives there), so this only re-skins the surrounding
 * MEMBER chrome:
 *   - LEGACY (flag off): byte-identical to the shipped soft-mint chrome — the
 *     indigo "Your parts are highlighted." orientation strip, the indigo
 *     italic footer caption, the mint visibility line.
 *   - EDITORIAL (flag on): the wireframe `V_B52` — a small floating `.t-card`
 *     pill ("your branch is highlighted; rest is read-only context") on the
 *     warm canvas, plus a warm-paper visibility line on a hairline. RE-SKIN,
 *     not rebuild: every `data-testid`, copy, and behaviour is preserved.
 */

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";
import type { TreeNode } from "../tree/buildTree";
import { canAuthor as canAuthorNode, indexById } from "../tree/canAuthor";
import { PALETTE } from "../tree/palette";
import { TaskCanvas } from "./TaskCanvas";

const INDIGO = "#4F46E5";

export interface MemberFullMapProps {
  roots: TreeNode[];
  members: TaskMember[];
  collapsed: ReadonlySet<string>;
  selectedId: string | null;
  /** The current member's membership id (drives "my parts"), or null. */
  membershipId: string | null;
  onSelect: (id: string | null) => void;
  onToggleCollapse: (id: string) => void;
  /** Open a node's detail drawer (B5.3). */
  onOpenDrawer: (id: string) => void;
}

/**
 * Compute "my parts": the nodes assigned to the member, PLUS every descendant
 * beneath those nodes (an assignee owns the subtree — §A5). These are the
 * cards the full map keeps bright + indigo.
 */
export function computeMyParts(
  roots: readonly TreeNode[],
  membershipId: string | null,
): Set<string> {
  const parts = new Set<string>();
  if (!membershipId) return parts;
  const markSubtree = (node: TreeNode) => {
    parts.add(node.id);
    for (const child of node.children) markSubtree(child);
  };
  for (const node of indexById(roots).values()) {
    if (node.assigneeIds.includes(membershipId)) markSubtree(node);
  }
  return parts;
}

export function MemberFullMap(props: MemberFullMapProps) {
  const { roots, members, collapsed, selectedId, membershipId, onSelect, onToggleCollapse } = props;
  const highlightIds = computeMyParts(roots, membershipId);

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";

  // Read-oriented: the canvas needs the full set of card callbacks, but the
  // member only opens nodes here (the drawer gates the rest). The editing
  // callbacks are no-ops — the surfaces that fire them (focus quick-actions,
  // drag-reparent) aren't reachable in this view (no `onReparent`, and a
  // member doesn't focus-edit on the read map).
  const noop = () => undefined;

  // Only show the orientation callout when the member has highlighted parts —
  // if they have none (nothing assigned), the canvas already shows the whole tree
  // without a "you" anchor, so there's nothing to orient them toward.
  const hasHighlights = highlightIds.size > 0;

  return (
    <div
      className="relative flex h-full flex-col"
      data-testid="member-full-map"
      style={{ fontFamily: editorial ? theme.fonts.sans : undefined }}
    >
      {/* Focal callout: a calm, single-line legend keyed to the highlight
          treatment (#932). Gives the member an anchor on a large org tree — they
          land on the map and immediately know which nodes are theirs.
          Brand: factual + calm; no peer-comparison; never evaluative.
          EDITORIAL: a warm hairline strip keyed to the wireframe `.t-node.lit`
          warm-cream + ink swatch; LEGACY: the shipped indigo strip. */}
      {hasHighlights && (
        <div
          data-testid="member-full-map-orientation"
          className="flex items-center gap-2.5 border-b px-5 py-2"
          style={
            editorial
              ? { borderColor: c.rule, backgroundColor: c.bgRecess }
              : { borderColor: PALETTE.softBorder, backgroundColor: "#F5F4FF" }
          }
        >
          <span
            className="inline-flex h-3 w-3 shrink-0"
            style={
              editorial
                ? { backgroundColor: "#FFFEF8", border: `1.5px solid ${c.ink}`, borderRadius: 3 }
                : { backgroundColor: INDIGO, borderRadius: 2 }
            }
            aria-hidden
          />
          <span className="text-[12px] font-semibold" style={{ color: editorial ? c.ink : INDIGO }}>
            Your parts are highlighted.
          </span>
          <span className="text-[12px]" style={{ color: editorial ? c.muted : "#6B7280" }}>
            The rest is visible for context — select any node to read it.
          </span>
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <TaskCanvas
          roots={roots}
          members={members}
          collapsed={collapsed}
          selectedId={selectedId}
          highlightIds={highlightIds}
          onSelect={onSelect}
          onToggleCollapse={onToggleCollapse}
          onSetState={noop}
          onAddSubtask={noop}
          onRename={noop}
          onArchive={noop}
          onAssignToggle={noop}
          onOpenDrawer={props.onOpenDrawer}
          // #1043 items 5/6/7 — the member full-map is a READ surface (its edit
          // callbacks are no-ops; the member edits via the drawer, gated by
          // `canAuthor`). Gate the new always-on card affordances by the SAME
          // §A5 recursive authorship rule so a non-author never sees a "+"/
          // assign/rename trigger that the backend would 403. This member is not
          // a lead in this view, so `isLead: false`.
          canAuthorNode={(node) => canAuthorNode(node, { membershipId, isLead: false }, roots)}
        />
        {/* Footer caption — verbatim from the gallery. EDITORIAL pulls it into
            the wireframe `V_B52` floating `.t-card` pill, top-left over the warm
            canvas; LEGACY keeps the centred indigo italic caption. */}
        {editorial ? (
          <div
            className="pointer-events-none absolute left-4 top-4 px-2.5 py-1.5 text-[11px]"
            style={{
              backgroundColor: c.cardBg,
              border: `1px solid ${c.rule}`,
              borderRadius: theme.radii.md,
              color: c.muted,
            }}
            data-testid="member-full-map-caption"
          >
            your branch is highlighted; rest is read-only context
          </div>
        ) : (
          <div
            className="pointer-events-none absolute bottom-12 left-1/2 -translate-x-1/2 text-center text-[11px] italic"
            style={{ color: INDIGO }}
            data-testid="member-full-map-caption"
          >
            your branch is highlighted; rest is read-only context
          </div>
        )}
      </div>

      {/* Visibility line (§A8 / trustable pillar) — factual. EDITORIAL: warm
          paper + mono hint ink on a hairline; LEGACY: the shipped mint line. */}
      <div
        className="border-t px-4 py-1.5 text-[11px]"
        style={
          editorial
            ? {
                borderColor: c.rule,
                backgroundColor: c.canvas,
                color: c.faint,
                fontFamily: theme.fonts.mono,
                letterSpacing: "0.02em",
              }
            : {
                borderColor: PALETTE.softBorder,
                backgroundColor: PALETTE.canvas,
                color: PALETTE.faint,
              }
        }
        data-testid="member-visibility-line"
      >
        Everyone on this team can see the whole map. Your parts are highlighted.
      </div>
    </div>
  );
}
